import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener,
         Inject, OnInit, ViewChild, ViewRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { SVG, Svg, G, Matrix } from '@svgdotjs/svg.js';
import { TextPlaceholder
       } from 'collage-designer/stage/placeholder/text-placeholder';
import { StageService } from 'collage-designer/stage/stage.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { ToolboxComponent } from '../toolbox.component';
import { ToolboxService } from '../toolbox.service';

interface AnimationFrame
{
  transformation?: string;
  opacity?: number;
  duration?: number;
  animated?: boolean;
  callback?: () => void; // eslint-disable-line
}

const _ANMIATION_GRACE_TIME = 80; /* ms */

@Component({
    selector: 'text-toolbox',
    templateUrl: './text-toolbox.component.html',
    styleUrls: ['../toolbox.component.scss', './text-toolbox.component.scss',],
    standalone: false
})
export class TextToolboxComponent
    extends ToolboxComponent implements AfterViewInit, OnInit
{
  private static readonly perspectiveGraceTime: number = 20; /* ms */
  private static readonly animationFrameDuration: number = 1000; /* ms */

  public placeholder: TextPlaceholder;
  public heightOverride: string = '';
  public hostElementTransformation: SafeStyle;
  public hostElementOpacity: number = 1;
  public hostElementTransition: string = 'none';

  @ViewChild('editor') private editor: ElementRef;

  constructor(
      mir: MatIconRegistry,
      sanitizer: DomSanitizer,
      private cdr: ChangeDetectorRef,
      @Inject(ToolboxService) toolboxService,
      @Inject(StageService) private stageService: StageService)
  {
    super(mir, sanitizer, toolboxService);
    this.placeholder = <TextPlaceholder> this.placeholder;
  }

  public editingResponse(response: string): void
  {
    this.hideEditableDiv();
    switch (response) {
      case 'confirm':
        this.startClosingAnimation(() => {
          this.btnOKClick();
          StageUtils.removeUnusedFonts(
              this.stageService.getCurrentStageReference());
        });
        break;
      default:
        this.startClosingAnimation(() => this.btnCancelClick());
        break;
    }
  }

  @HostListener('window:resize', [])
  public ngOnInit(): void
  {
    if (! Utils.isSmallDevice()) {
      return;
    }

    // Steve Jobs be damned!
    this.heightOverride = `${window.innerHeight}px`;
  }

  public ngAfterViewInit(): void
  {
    const textToolboxEditor = this.editor.nativeElement as HTMLElement;
    const inIframe = window.self !== window.top;

    if (inIframe) {
      textToolboxEditor.style.position = 'fixed';
    }

    textToolboxEditor.scrollIntoView(
      { behavior: 'smooth', block: 'center', inline: 'nearest' });
    this.startOpeningAnimationAt(this.placeholder.wrapper);
  }

  private hideEditableDiv() {
    const editableDiv = document.getElementById('editable');
    if (Utils.isSafari()) {
      editableDiv.classList.remove('show-div-safari');
    }
    else {
      editableDiv.classList.remove('show-div');
    }
  }

  private startClosingAnimation(onFinished: () => void): void
  {
    this.animate([
      { transformation:
          'perspective(0cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        animated: false,
        duration: _ANMIATION_GRACE_TIME,
      },
      { transformation:
        'perspective(20cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        animated: false,
        duration: TextToolboxComponent.perspectiveGraceTime,
      },
      { transformation:
          'perspective(20cm) rotateX(10deg) rotateY(0deg) scale(0.85)',
        opacity: 0,
        duration: 450,
      },
      {
        callback: onFinished,
      }
    ]);
  }

  private setTransformation(transformation: string): void
  {
    this.hostElementTransformation =
      this.sanitizer.bypassSecurityTrustStyle(transformation);
  }

  private setOpacity(opacity: number): void
  {
    this.hostElementOpacity = opacity;
  }

  private setTransition(duration: number | undefined): void
  {
    this.hostElementTransition =
      duration !== undefined ? `${duration / 1000}s` : 'none';
  }

  private startOpeningAnimationAt(wrapper: Svg): void
  {
    if (Utils.isSmallDevice()) {
      this.startFadeInEffect();
    } else {
      this.startWhoooooooshEffect(wrapper);
    }
  }

  private startFadeInEffect(): void
  {
    this.animate([
      { transformation:
          'perspective(20cm) rotateX(10deg) rotateY(0deg) scale(0.85)',
        opacity: 0,
        animated: false,
        duration: _ANMIATION_GRACE_TIME,
      },
      { transformation:
          'perspective(0cm) rotateX(0deg) rotateY(0deg) scale(1)',
        opacity: 1,
        duration: 450,
      },
    ]);
  }

  private startWhoooooooshEffect(wrapper: Svg): void
  {
    const frame = (<any>wrapper).parent() as G;

    const rotation = frame.transform().rotate || 0;
    const rotationMatrix = new Matrix().rotate(-rotation);

    const source = this.editor.nativeElement.getBoundingClientRect();
    const target = wrapper.rbox();
    target.x -= window.pageXOffset;
    target.y -= window.pageYOffset;

    const mid = (coord: number, dimension: number) => coord + dimension / 2;
    const offsetX =
      mid(target.x, target.width) - mid(source.left, source.width);
    const offsetY =
      mid(target.y, target.height) - mid(source.top, source.height);

    const rotatedTarget = target.transform(rotationMatrix);
    const scaleX = rotatedTarget.width / source.width;
    const scaleY = rotatedTarget.width / source.width;
    const scale = Math.max(scaleX, scaleY);

    const normalizedRotation =
      Math.abs(rotation) <= 180 ? rotation : -(rotation % 180);
    this.animate([
      { transformation:
          `translate(${offsetX}px, ${offsetY}px) ` +
          `scale(${scale}) ` +
          `rotate(${normalizedRotation}deg) `,
        opacity: 0,
        animated: false,
        duration: _ANMIATION_GRACE_TIME,
      },

      { transformation:
          `translate(0px, 0px) ` +
          `scale(1) ` +
          `rotate(0deg)`,
        opacity: 1,
      }
    ]);
  }

  private animate(steps: Array<AnimationFrame>): void
  {
    const next = steps.shift();

    if (! next) {
      this.setTransition(undefined);
      return;
    }

    const transformation = next.transformation;
    const opacity = next.opacity;
    const duration =
      next.duration || TextToolboxComponent.animationFrameDuration;
    const animated = next.animated === undefined ? true : next.animated;
    const callback = next.callback;

    this.setTransition(animated ? duration : undefined);

    if (transformation !== undefined) {
      this.setTransformation(transformation);
    }

    if (opacity !== undefined) {
      this.setOpacity(opacity);
    }

    if (callback !== undefined) {
      callback();
    }

    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }

    setTimeout(() => this.animate(steps), duration);
  }
}
